/**
 * A vitrine do Design Moderno — `/padrao` (onda 321, spec 08 §4, RN-258).
 *
 * Cada peça da base (`design/componentes.css`) em cada estado, montada
 * com o código de verdade — os mesmos componentes e as mesmas classes que
 * as telas usam. É a referência de quem faz tela nova: na bancada, no
 * Portal ou num módulo do ERP. Peça que falta nasce na base e AQUI antes
 * de ir à tela (RN-258 c).
 *
 * Aberta a todo usuário interno (D4, 18/10): é referência de trabalho, sem
 * dado de cliente. Nada aqui grava no banco.
 */
import { useState } from 'react';
import { Aviso, BarraDeAvanco, Campo, Dialogo, EscolhaPorCodigo, Selo, SeloDoEnsaio } from '../ui';
import { Icone, type NomeDoIcone } from '../Icone';

const ICONES: NomeDoIcone[] = [
  'os', 'fila', 'estufa', 'receber', 'mais', 'amostras', 'qr', 'busca', 'painel', 'cronograma',
  'verificar', 'relatorio', 'equipamento', 'descarte', 'mapa', 'medicao', 'campo', 'pessoa', 'tema',
  'sair', 'filtro', 'fechar', 'normas', 'frasco', 'ok', 'alerta', 'ajustes', 'exportar', 'cadastros', 'cone',
];

export function Padrao() {
  const [aba, setAba] = useState('hoje');
  const [escolha, setEscolha] = useState('aceite');
  const [janela, setJanela] = useState(false);
  const [capsula, setCapsula] = useState('');
  const [balanca, setBalanca] = useState('b4');

  return (
    <div className="vitrine">
      <header className="vitrine-cabeca">
        <h1>O padrão visual</h1>
        <p className="sub">
          O Design Moderno (RN-258): as peças da base, em cada estado. Toda tela — da bancada, do
          Portal e de cada módulo do ERP — monta com estas peças. Troque o tema na engrenagem para
          ver o escuro.
        </p>
      </header>

      <section className="cartao" aria-labelledby="v-botoes">
        <h2 id="v-botoes">Botões</h2>
        <div className="vitrine-linha">
          <button type="button" className="botao">Gravar</button>
          <button type="button" className="botao secundario">Cancelar</button>
          <button type="button" className="botao discreto">Ver detalhes</button>
          <button type="button" className="botao perigo">Descartar</button>
          <button type="button" className="botao" disabled>Aprovar</button>
          <button type="button" className="botao secundario" aria-disabled="true">Emitir laudo</button>
        </div>
        <p className="sub">Uma ação principal por tela. O indisponível tem camada própria e diz por quê ao lado.</p>
      </section>

      <section className="cartao" aria-labelledby="v-campos">
        <h2 id="v-campos">Campos e escolha</h2>
        <div className="vitrine-grade">
          <Campo rotulo="Cápsula" defaultValue="CAP-297-01" dica="O código gravado na cápsula." />
          <Campo rotulo="Cápsula + solo seco (g)" defaultValue="12,5" inputMode="decimal"
            erro="Menor que a cápsula vazia (38,21 g). Confira a pesagem." />
          <Campo rotulo="Balança" defaultValue="BAL-04" disabled dica="Escolhida pela bancada." />
        </div>
        {/* A escolha pelo número (349): a mesma letra do campo, a lista por cima. */}
        <div className="vitrine-grade">
          <EscolhaPorCodigo rotulo="Cápsula (escolha pelo número)" valor={capsula} aoEscolher={setCapsula}
            opcoes={[
              { valor: 'c118', codigo: '118', detalhe: 'tara 31,42 g' },
              { valor: 'c119', codigo: '119', detalhe: 'tara 30,87 g' },
              { valor: 'c218', codigo: '218', detalhe: 'tara 32,05 g' },
            ]} dica="Digite o número gravado na cápsula." />
          <EscolhaPorCodigo rotulo="Balança" valor={balanca} aoEscolher={setBalanca}
            opcoes={[{ valor: 'b4', codigo: 'BAL-04', detalhe: 'Balança 3,1 kg × 0,01 g' }]} />
          <EscolhaPorCodigo rotulo="Cápsula (com erro)" valor="" aoEscolher={() => undefined}
            opcoes={[{ valor: 'c118', codigo: '118' }]} erro="Escolha a cápsula antes de gravar." />
          <EscolhaPorCodigo rotulo="Balança (indisponível)" valor="b4" aoEscolher={() => undefined}
            opcoes={[{ valor: 'b4', codigo: 'BAL-04', detalhe: 'Balança 3,1 kg × 0,01 g' }]} desabilitado />
        </div>
        {/* A escolha pelo NOME (352, RN-273): larga, em ordem alfabética, afunilando. */}
        <EscolhaPorCodigo rotulo="Categoria (escolha pelo nome)" valor="" aoEscolher={() => undefined}
          largo placeholder="digite para filtrar a categoria…"
          opcoes={[
            { valor: 'balanca', codigo: 'Balança', detalhe: 'Instrumento' },
            { valor: 'balao', codigo: 'Balão volumétrico', detalhe: 'Item calibrado internamente' },
            { valor: 'cbr', codigo: 'Cilindro CBR', detalhe: 'Item calibrado internamente' },
          ]} dica="A lista vem em ordem alfabética; digite parte do nome para afunilar." />
        <div className="escolha" role="group" aria-label="Decisão do recebimento">
          {[['aceite', 'Aceite'], ['ressalva', 'Aceite com ressalva'], ['rejeicao', 'Rejeição']].map(([v, t]) => (
            <button key={v} type="button" aria-pressed={escolha === v} onClick={() => setEscolha(v)}>{t}</button>
          ))}
        </div>
      </section>

      <section className="cartao" aria-labelledby="v-selos">
        <h2 id="v-selos">Selos e chips</h2>
        <div className="vitrine-linha">
          <Selo tom="ok">Aprovado</Selo>
          <Selo tom="andamento">Na bancada</Selo>
          <Selo tom="atencao">Esperando verificação</Selo>
          <Selo tom="critico">Devolvido</Selo>
          <Selo tom="neutro">Cancelado</Selo>
        </div>
        <div className="vitrine-linha">
          <SeloDoEnsaio status="em_execucao" />
          <SeloDoEnsaio status="aprovado" />
        </div>
        <div className="vitrine-linha">
          <span className="chip ok">Conectado</span>
          <span className="chip atencao">Reenviando</span>
          <span className="chip alerta">Sem conexão</span>
          <span className="chip">Neutro</span>
        </div>
        <p className="sub">O ponto guarda a forma (● ▲ ■ ◐) e a palavra está sempre escrita: a cor nunca sozinha.</p>
      </section>

      <section className="cartao" aria-labelledby="v-abas">
        <h2 id="v-abas">Abas</h2>
        <div className="abas" role="tablist" aria-label="Exemplo de abas">
          {[['hoje', 'Hoje'], ['andamento', 'Andamento'], ['historico', 'Histórico']].map(([v, t]) => (
            <button key={v} type="button" role="tab" aria-selected={aba === v}
              className={aba === v ? 'ativa' : undefined} onClick={() => setAba(v)}>{t}</button>
          ))}
        </div>
      </section>

      <section className="cartao" aria-labelledby="v-tabela">
        <h2 id="v-tabela">Tabela</h2>
        <div className="rolagem">
          <table className="dados">
            <thead><tr><th>Ponto</th><th className="num">Golpes</th><th className="num">w (%)</th><th>Situação</th></tr></thead>
            <tbody>
              <tr><td>1</td><td className="num">34</td><td className="num">41,2</td><td><Selo tom="ok">Gravada</Selo></td></tr>
              <tr><td>2</td><td className="num">27</td><td className="num">43,8</td><td><Selo tom="ok">Gravada</Selo></td></tr>
              <tr><td>3</td><td className="num">19</td><td className="num">47,5</td><td><Selo tom="atencao">Fora da reta</Selo></td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="cartao" aria-labelledby="v-avisos">
        <h2 id="v-avisos">Avisos, passos e progresso</h2>
        <Aviso tipo="erro" titulo="2 ensaios sem verificador habilitado">Nenhum dos dois pode ir a laudo.</Aviso>
        <Aviso tipo="atencao" titulo="Há mais de 24 h na estufa">3 cápsulas esperando a pesagem seca.</Aviso>
        <Aviso tipo="ok" titulo="Pesagem gravada">CAP-297-01 · w = 15,70 %</Aviso>
        <Aviso tipo="info">Explicação do funcionamento normal — sem cor de alarme.</Aviso>
        <ol className="passos" aria-label="Passos de exemplo">
          <li className="feito">1 · Identificação</li>
          <li aria-current="step">2 · Amostras</li>
          <li>3 · Revisão</li>
        </ol>
        <BarraDeAvanco pct={60} rotulo="Avanço de exemplo" />
      </section>

      <section className="cartao" aria-labelledby="v-janela">
        <h2 id="v-janela">Janela</h2>
        <button type="button" className="botao secundario" onClick={() => setJanela(true)}>Abrir a janela</button>
        <Dialogo aberto={janela} titulo="Devolver o ensaio para correção?" rotulo="Vitrine"
          aoFechar={() => setJanela(false)}>
          <p>O técnico recebe o motivo e o ensaio volta para a Fila. Nada do que foi lançado se apaga.</p>
          <Campo rotulo="Motivo (mínimo 10 letras)" defaultValue="Ponto 3 fora da reta" />
          <div className="rodape-dialogo">
            <button type="button" className="botao secundario" onClick={() => setJanela(false)}>Cancelar</button>
            <button type="button" className="botao" onClick={() => setJanela(false)}>Devolver</button>
          </div>
        </Dialogo>
      </section>

      <section className="cartao" aria-labelledby="v-icones">
        <h2 id="v-icones">Ícones</h2>
        <ul className="vitrine-icones">
          {ICONES.map((n) => (
            <li key={n}><Icone nome={n} /><span className="mono">{n}</span></li>
          ))}
        </ul>
      </section>
    </div>
  );
}
